import { buildQuery } from '@calendly/http-tools'

import { buildChromeWebstoreURL } from 'components/integrations/urls'

export const INTEGRATION_KEYS = {
  androidApp: 'android_app',
  apiWebhooks: 'api_webhooks',
  chrome: 'chrome',
  duo: 'duo',
  edge: 'edge',
  exchangeCalendar: 'exchange_calendar',
  facebookPixel: 'facebook_pixel',
  firefox: 'firefox',
  gmail: 'gmail',
  gong: 'gong',
  googleAnalytics: 'google_analytics',
  googleCalendar: 'google_calendar',
  googleMeet: 'google_meet',
  gotomeeting: 'gotomeeting',
  hubspot: 'hubspot',
  icloudCalendar: 'icloud_calendar',
  intercom: 'intercom',
  iphoneApp: 'iphone_app',
  lever: 'lever',
  linkedin: 'linkedin',
  mailchimp: 'mailchimp',
  marketo: 'marketo',
  microsoftAzure: 'microsoft_azure',
  microsoftDynamics: 'microsoft_dynamics',
  microsoftPowerAutomate: 'microsoft_power_automate',
  microsoftTeams: 'microsoft_teams',
  microsoftTeamsChat: 'microsoft_teams_chat',
  office365: 'office_365',
  office365Outlook: 'office_365_outlook',
  office365OutlookOrganization: 'office_365_outlook_organization',
  okta: 'okta',
  oneLogin: 'one_login',
  outlook: 'outlook',
  outlookCalendar: 'outlook_calendar',
  outlookExtension: 'outlook_extension',
  pardot: 'pardot',
  paypal: 'paypal',
  pingIdentity: 'ping_identity',
  safari: 'safari',
  salesforce: 'salesforce',
  slack: 'slack',
  squarespace: 'squarespace',
  ssoAndScim: 'sso_and_scim',
  stripe: 'stripe',
  webex: 'webex',
  wix: 'wix',
  wordpress: 'wordpress',
  zapier: 'zapier',
  zoom: 'zoom',
}

export const MICROSOFT_TEAMS_CHAT_APP_URL = 'https://appsource.microsoft.com/en-us/product/office/WA200006736?tab=Overview'

export const CHROME_APP_URL = buildChromeWebstoreURL({
  utmSource: 'calendly',
  utmMedium: 'integrations',
  utmCampaign: 'calendly_for_chrome',
  utmContent: 'chrome',
  utmTerm: 'chrome',
})

const outlookExtensionURL = `https://appsource.microsoft.com/en-us/product/office/WA104381446${buildQuery({
  utm_source: 'calendly',
  utm_medium: 'integrations',
  utm_campaign: 'integrations',
  utm_content: 'outlook_addin',
  utm_term: 'outlook_addin',
}, { addQueryPrefix: true })}`

// Category tags used for integration search
const VIDEO_CONFERENCING = 'video conferencing'
const CALENDAR = 'calendar'
const EXTENSION_APP = 'extension app'
const SALES_CRM = 'sales crm'
const MARKETING = 'marketing'
const EMAIL_MESSAGING = 'email message messaging'
const ANALYTICS = 'analytics'
const EMBED = 'embed'
const PAYMENT = 'payment'
const SECURITY = 'sso security single sign on'
const API_CONNECTOR = 'api connector webhook'

// Extra tags used for integration search
const APPLE = 'apple ios'
const FACEBOOK = 'meta facebook'
const GOOGLE = 'google'
const MICROSOFT = 'microsoft office'
const MOZILLA = 'mozilla'
const ROUTING = 'route routing form'

/**
 * @typedef {Object} IntegrationTileProperties
 * @property {string} key
 * @property {string} [to] - If absent, tile renderer will use `/integrations/<key>` instead
 * @property {string} [data_analytics_id] - If absent, tile renderer will use the key property instead
 * @property {string} [html_anchor_id]
 * @property {string} [provider_logo] - If absent, tile renderer will use the key property instead
 * @property {('server_side_render' | 'tracked_click', | 'coming_soon')} [variation] - There are some tiles that behave
 *     differently from the usual link
 * @property {string} [href] - Only used when the variation is 'server_side_render' or 'tracked_click'
 * @property {Object} [segment] - Only used when the variation is 'tracked_click'
 * @property {boolean} [new_tab] - Only used when the href should open in new tab
 * @property {boolean} [admin] - If enabled, displays an "ADMIN" badge on the tile
 * @property {boolean} [admin_required] - If enabled, displays an "Admin Required" popup to non-admins/non-owners on tile click
 * @property {string[]} [tags] - Tags used for integration search
 */

export const INTEGRATIONS = {
  [INTEGRATION_KEYS.gotomeeting]: {
    key: INTEGRATION_KEYS.gotomeeting,
    provider_logo: 'go-to-meeting',
    tags: [VIDEO_CONFERENCING],
  },
  [INTEGRATION_KEYS.hubspot]: {
    key: INTEGRATION_KEYS.hubspot,
    admin: true,
    tags: [SALES_CRM, MARKETING]
  },
  [INTEGRATION_KEYS.mailchimp]: {
    key: INTEGRATION_KEYS.mailchimp,
    admin: true,
    tags: [MARKETING, EMAIL_MESSAGING],
  },
  [INTEGRATION_KEYS.salesforce]: {
    key: INTEGRATION_KEYS.salesforce,
    admin: true,
    tags: [SALES_CRM],
  },
  [INTEGRATION_KEYS.stripe]: {
    key: INTEGRATION_KEYS.stripe,
    tags: [PAYMENT],
  },
  [INTEGRATION_KEYS.webex]: {
    key: INTEGRATION_KEYS.webex,
    tags: [VIDEO_CONFERENCING],
  },
  [INTEGRATION_KEYS.zapier]: {
    key: INTEGRATION_KEYS.zapier,
    tags: [API_CONNECTOR],
  },
  [INTEGRATION_KEYS.zoom]: {
    key: INTEGRATION_KEYS.zoom,
    tags: [VIDEO_CONFERENCING],
  },
  [INTEGRATION_KEYS.lever]: {
    key: INTEGRATION_KEYS.lever,
    admin: true,
    admin_required: true,
    tags: [EXTENSION_APP],
  },
  [INTEGRATION_KEYS.linkedin]: {
    key: INTEGRATION_KEYS.linkedin,
    data_analytics_id: 'calendly_for_linkedin',
    tags: [EXTENSION_APP, SALES_CRM, MICROSOFT],
  },
  [INTEGRATION_KEYS.microsoftTeams]: {
    key: INTEGRATION_KEYS.microsoftTeams,
    provider_logo: 'microsoft-teams',
    tags: [VIDEO_CONFERENCING, MICROSOFT],
  },
  [INTEGRATION_KEYS.googleMeet]: {
    key: INTEGRATION_KEYS.googleMeet,
    provider_logo: 'google-meet',
    tags: [VIDEO_CONFERENCING, GOOGLE],
  },
  [INTEGRATION_KEYS.gmail]: {
    key: INTEGRATION_KEYS.gmail,
    tags: [EMAIL_MESSAGING, GOOGLE],
  },
  [INTEGRATION_KEYS.outlook]: {
    key: INTEGRATION_KEYS.outlook,
    tags: [EMAIL_MESSAGING, MICROSOFT],
  },
  [INTEGRATION_KEYS.paypal]: {
    key: INTEGRATION_KEYS.paypal,
    provider_logo: 'pay-pal',
    tags: [PAYMENT],
  },
  [INTEGRATION_KEYS.intercom]: {
    key: INTEGRATION_KEYS.intercom,
    data_analytics_id: 'calendly_for_intercom',
    tags: [EXTENSION_APP, MARKETING, EMBED],
  },
  [INTEGRATION_KEYS.googleAnalytics]: {
    key: INTEGRATION_KEYS.googleAnalytics,
    provider_logo: 'google-analytics',
    admin: true,
    tags: [MARKETING, ANALYTICS, GOOGLE],
  },
  [INTEGRATION_KEYS.facebookPixel]: {
    key: INTEGRATION_KEYS.facebookPixel,
    provider_logo: 'facebook',
    admin: true,
    tags: [MARKETING, ANALYTICS, FACEBOOK],
  },
  [INTEGRATION_KEYS.apiWebhooks]: {
    key: INTEGRATION_KEYS.apiWebhooks,
    html_anchor_id: 'integration-tile-api-webhooks',
    provider_logo: 'console',
    tags: [API_CONNECTOR],
  },
  [INTEGRATION_KEYS.chrome]: {
    key: INTEGRATION_KEYS.chrome,
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'calendly_for_chrome' },
    },
    href: CHROME_APP_URL,
    new_tab: true,
    data_analytics_id: 'calendly_for_chrome',
    tags: [EXTENSION_APP, GOOGLE],
  },
  [INTEGRATION_KEYS.slack]: {
    key: INTEGRATION_KEYS.slack,
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'Slack' },
    },
    new_tab: true,
    tags: [EXTENSION_APP],
  },
  [INTEGRATION_KEYS.wix]: {
    key: INTEGRATION_KEYS.wix,
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'wix' }
    },
    href: 'https://help.calendly.com/hc/en-us/articles/223147127-How-to-add-Calendly-to-a-Wix-site',
    new_tab: true,
    tags: [EMBED],
  },
  [INTEGRATION_KEYS.wordpress]: {
    key: INTEGRATION_KEYS.wordpress,
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'wordpress' }
    },
    href: 'https://help.calendly.com/hc/en-us/articles/223195568#how-to-embed-calendly-in-wordpress-0-0',
    new_tab: true,
    tags: [EMBED],
  },
  [INTEGRATION_KEYS.exchangeCalendar]: {
    key: INTEGRATION_KEYS.exchangeCalendar,
    variation: 'server_side_render',
    href: '/app/calendar_connections',
    provider_logo: 'microsoft-exchange',
    tags: [CALENDAR, MICROSOFT],
  },
  [INTEGRATION_KEYS.googleCalendar]: {
    key: INTEGRATION_KEYS.googleCalendar,
    variation: 'server_side_render',
    href: '/app/calendar_connections',
    provider_logo: 'google-calendar',
    tags: [CALENDAR, GOOGLE],
  },
  [INTEGRATION_KEYS.icloudCalendar]: {
    key: INTEGRATION_KEYS.icloudCalendar,
    variation: 'server_side_render',
    href: '/app/calendar_connections',
    provider_logo: 'icloud',
    tags: [CALENDAR, APPLE],
  },
  [INTEGRATION_KEYS.office365]: {
    key: INTEGRATION_KEYS.office365,
    variation: 'server_side_render',
    href: '/app/calendar_connections',
    provider_logo: 'microsoft-outlook',
    tags: [CALENDAR, MICROSOFT],
  },
  [INTEGRATION_KEYS.office365Outlook]: {
    key: INTEGRATION_KEYS.office365Outlook,
    variation: 'server_side_render',
    href: '/app/calendar_connections',
    provider_logo: 'microsoft-outlook',
    tags: [CALENDAR, MICROSOFT],
  },
  [INTEGRATION_KEYS.office365OutlookOrganization]: {
    key: INTEGRATION_KEYS.office365OutlookOrganization,
    href: '/integrations/office_365_outlook_organization',
    provider_logo: 'microsoft-outlook',
    tags: [CALENDAR, MICROSOFT],
  },
  [INTEGRATION_KEYS.outlookCalendar]: {
    key: INTEGRATION_KEYS.outlookCalendar,
    variation: 'server_side_render',
    href: '/app/calendar_connections',
    provider_logo: 'microsoft-outlook',
    tags: [CALENDAR, MICROSOFT],
  },
  [INTEGRATION_KEYS.iphoneApp]: {
    key: INTEGRATION_KEYS.iphoneApp,
    provider_logo: 'apple',
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'iphone_app' }
    },
    href: `https://apps.apple.com/app/apple-store/id1451094657${buildQuery({
      pt: '118425599',
      mt: '8',
      ct: 'integrations',
      utm_source: 'calendly',
      utm_medium: 'integrations',
      utm_campaign: 'integrations',
      utm_content: 'ios',
      utm_term: 'ios',
    }, { addQueryPrefix: true })}`,
    new_tab: true,
    tags: [EXTENSION_APP, APPLE],
  },
  [INTEGRATION_KEYS.androidApp]: {
    key: INTEGRATION_KEYS.androidApp,
    variation: 'tracked_click',
    provider_logo: 'android',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'android_app' }
    },
    href: `https://play.google.com/store/apps/details${buildQuery({
      id: 'com.calendly.app',
      hl: 'en_US',
      gl: 'US',
      utm_source: 'calendly',
      utm_medium: 'integrations',
      utm_campaign: 'integrations',
      utm_content: 'android',
      utm_term: 'android',
    }, { addQueryPrefix: true })}`,
    new_tab: true,
    tags: [EXTENSION_APP, GOOGLE],
  },
  [INTEGRATION_KEYS.squarespace]: {
    key: INTEGRATION_KEYS.squarespace,
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'squarespace' }
    },
    href: 'https://help.calendly.com/hc/en-us/articles/223147047-How-to-add-Calendly-to-your-Squarespace-site',
    new_tab: true,
    tags: [EMBED],
  },
  [INTEGRATION_KEYS.safari]: {
    key: INTEGRATION_KEYS.safari,
    provider_logo: 'safari',
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'safari' }
    },
    href: `https://apps.apple.com/us/app/calendly-meeting-scheduling/id6746816242${buildQuery({
      utm_source: 'calendly',
      utm_medium: 'integrations',
      utm_campaign: 'integrations',
      utm_content: 'safari',
      utm_term: 'safari',
    }, { addQueryPrefix: true })}`,
    new_tab: true,
    tags: [EXTENSION_APP, APPLE],
  },
  [INTEGRATION_KEYS.firefox]: {
    key: INTEGRATION_KEYS.firefox,
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'firefox' }
    },
    href: `https://addons.mozilla.org/en-US/firefox/addon/calendly-meeting-scheduling${buildQuery({
      utm_source: 'calendly',
      utm_medium: 'integrations',
      utm_campaign: 'integrations',
      utm_content: 'firefox',
      utm_term: 'firefox',
    }, { addQueryPrefix: true })}`,
    new_tab: true,
    tags: [EXTENSION_APP, MOZILLA],
  },
  [INTEGRATION_KEYS.edge]: {
    key: INTEGRATION_KEYS.edge,
    provider_logo: 'microsoft-edge',
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'edge' }
    },
    href: `https://microsoftedge.microsoft.com/addons/detail/calendly-meeting-schedul/hfngfpepejakabkhocoamdlnipilcpga${buildQuery({
      utm_source: 'calendly',
      utm_medium: 'integrations',
      utm_campaign: 'integrations',
      utm_content: 'edge',
      utm_term: 'edge',
    }, { addQueryPrefix: true })}`,
    new_tab: true,
    tags: [EXTENSION_APP, MICROSOFT],
  },
  [INTEGRATION_KEYS.outlookExtension]: {
    key: INTEGRATION_KEYS.outlookExtension,
    provider_logo: 'outlookExtension',
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'outlook_extension' }
    },
    data_analytics_id: 'outlook_extension',
    href: outlookExtensionURL,
    new_tab: true,
    tags: [EXTENSION_APP, MICROSOFT],
  },
  [INTEGRATION_KEYS.okta]: {
    key: INTEGRATION_KEYS.okta,
    variation: 'tracked_click',
    admin: true,
    admin_required: true,
    href: '/app/admin/login/sso',
    segment: {
      event: 'sso_integration_tile.click',
      payload: {
        name_of_idp: INTEGRATION_KEYS.okta,
      }
    },
    tags: [SECURITY],
  },
  [INTEGRATION_KEYS.microsoftAzure]: {
    key: INTEGRATION_KEYS.microsoftAzure,
    provider_logo: 'microsoft-azure',
    variation: 'tracked_click',
    admin: true,
    admin_required: true,
    href: '/app/admin/login/sso',
    segment: {
      event: 'sso_integration_tile.click',
      payload: {
        name_of_idp: INTEGRATION_KEYS.microsoftAzure,
      }
    },
    tags: [SECURITY, MICROSOFT],
  },
  [INTEGRATION_KEYS.oneLogin]: {
    key: INTEGRATION_KEYS.oneLogin,
    provider_logo: 'one-login',
    variation: 'tracked_click',
    admin: true,
    admin_required: true,
    href: '/app/admin/login/sso',
    segment: {
      event: 'sso_integration_tile.click',
      payload: {
        name_of_idp: INTEGRATION_KEYS.oneLogin,
      }
    },
    tags: [SECURITY],
  },
  [INTEGRATION_KEYS.pingIdentity]: {
    key: INTEGRATION_KEYS.pingIdentity,
    provider_logo: 'ping-identity',
    variation: 'tracked_click',
    admin: true,
    admin_required: true,
    href: '/app/admin/login/sso',
    segment: {
      event: 'sso_integration_tile.click',
      payload: {
        name_of_idp: INTEGRATION_KEYS.pingIdentity,
      }
    },
    tags: [SECURITY],
  },
  [INTEGRATION_KEYS.duo]: {
    key: INTEGRATION_KEYS.duo,
    variation: 'tracked_click',
    admin: true,
    admin_required: true,
    href: '/app/admin/login/sso',
    segment: {
      event: 'sso_integration_tile.click',
      payload: {
        name_of_idp: INTEGRATION_KEYS.duo,
      }
    },
    tags: [SECURITY],
  },
  [INTEGRATION_KEYS.ssoAndScim]: {
    key: INTEGRATION_KEYS.ssoAndScim,
    provider_logo: 'sso',
    variation: 'tracked_click',
    admin: true,
    admin_required: true,
    href: '/app/admin/login/sso',
    segment: {
      event: 'sso_integration_tile.click',
      payload: {
        name_of_idp: INTEGRATION_KEYS.ssoAndScim,
      }
    },
    tags: [SECURITY],
  },
  [INTEGRATION_KEYS.marketo]: {
    key: INTEGRATION_KEYS.marketo,
    admin: true,
    tags: [MARKETING, ROUTING],
  },
  [INTEGRATION_KEYS.pardot]: {
    key: INTEGRATION_KEYS.pardot,
    provider_logo: 'salesforce-pardot',
    admin: true,
    tags: [MARKETING, ROUTING],
  },
  [INTEGRATION_KEYS.microsoftDynamics]: {
    key: INTEGRATION_KEYS.microsoftDynamics,
    admin: true,
    provider_logo: 'microsoft-dynamics',
    tags: [SALES_CRM, MICROSOFT],
  },
  [INTEGRATION_KEYS.microsoftPowerAutomate]: {
    key: INTEGRATION_KEYS.microsoftPowerAutomate,
    admin: true,
    provider_logo: 'microsoft-power-automate',
    tags: [API_CONNECTOR, MICROSOFT],
  },
  [INTEGRATION_KEYS.gong]: {
    key: INTEGRATION_KEYS.gong,
    tags: [SALES_CRM],
  },
  [INTEGRATION_KEYS.microsoftTeamsChat]: {
    key: INTEGRATION_KEYS.microsoftTeamsChat,
    provider_logo: 'microsoft-teams',
    href: MICROSOFT_TEAMS_CHAT_APP_URL,
    new_tab: true,
    variation: 'tracked_click',
    segment: {
      event: 'Integration Connected - Client',
      payload: { integration_type: 'ms_teams_chat' },
    },
    tags: [EXTENSION_APP, MICROSOFT],
  },
}

// Until the frontend/backend integration names are in sync, this function
// maps the backend names to those used on the frontend
export const getFrontendIntegrationName = (name) => {
  const integrations = {
    api_webhooks: INTEGRATION_KEYS.apiWebhooks,
    duo: INTEGRATION_KEYS.duo,
    exchange_calendar: INTEGRATION_KEYS.exchangeCalendar,
    gmail_for_workflows: INTEGRATION_KEYS.gmail,
    google_analytics: INTEGRATION_KEYS.googleAnalytics,
    google_calendar: INTEGRATION_KEYS.googleCalendar,
    google_meet: INTEGRATION_KEYS.googleMeet,
    gotomeeting: INTEGRATION_KEYS.gotomeeting,
    hubspot: INTEGRATION_KEYS.hubspot,
    icloud_calendar: INTEGRATION_KEYS.icloudCalendar,
    intercom: INTEGRATION_KEYS.intercom,
    lever: INTEGRATION_KEYS.lever,
    mailchimp: INTEGRATION_KEYS.mailchimp,
    marketo: INTEGRATION_KEYS.marketo,
    meta_pixel: INTEGRATION_KEYS.facebookPixel,
    microsoft_azure: INTEGRATION_KEYS.microsoftAzure,
    microsoft_teams: INTEGRATION_KEYS.microsoftTeams,
    office_365: INTEGRATION_KEYS.office365,
    office_365_outlook: INTEGRATION_KEYS.office365Outlook,
    office_365_outlook_organization: INTEGRATION_KEYS.office365OutlookOrganization,
    okta: INTEGRATION_KEYS.okta,
    one_login: INTEGRATION_KEYS.oneLogin,
    outlook_calendar: INTEGRATION_KEYS.outlookCalendar,
    outlook_for_workflows: INTEGRATION_KEYS.outlook,
    pardot: INTEGRATION_KEYS.pardot,
    paypal: INTEGRATION_KEYS.paypal,
    ping_identity: INTEGRATION_KEYS.pingIdentity,
    salesforce: INTEGRATION_KEYS.salesforce,
    slack: INTEGRATION_KEYS.slack,
    sso_and_scim: INTEGRATION_KEYS.ssoAndScim,
    stripe: INTEGRATION_KEYS.stripe,
    webex: INTEGRATION_KEYS.webex,
    zapier: INTEGRATION_KEYS.zapier,
    zoom: INTEGRATION_KEYS.zoom,
  }

  return integrations[name] || 'INVALID'
}

export const FIRST_PARTY_INTEGRATIONS_TYPE = 'first-party'
export const THIRD_PARTY_INTEGRATIONS_TYPE = 'third-party'
